/**
 * Stripe Connect approval — seller requests and super-admin actions via payment API.
 */

import { Platform } from 'react-native';
import { STRIPE_CONFIG } from '../config/stripeConfig';
import { getAdminAuthHeader } from './adminApiService';
import { MERCHANT_PAYMENT_TERMS_VERSION } from '../constants/merchantPaymentTerms';
import {
  StripeConnectApprovalRecord,
  StripeConnectApprovalStatus,
  StripeConnectRequestSnapshot,
} from '../types/stripeConnectApproval';
import { MerchantPaymentTermsAcceptance } from './merchantTermsService';
import { AuthManager, BusinessOwner } from '../utils/authManager';

const API_BASE = STRIPE_CONFIG.apiEndpoint;

async function parseJson(res: Response) {
  return res.json().catch(() => ({}));
}

export interface StripeConnectApprovalStatusResponse {
  success: boolean;
  approval?: StripeConnectApprovalRecord | null;
  canConnectStripe?: boolean;
  error?: string;
}

export interface StripeConnectRequestListItem {
  businessId: string;
  businessName: string;
  approval: StripeConnectApprovalRecord;
}

export async function fetchStripeConnectApproval(
  businessId: string
): Promise<StripeConnectApprovalStatusResponse> {
  try {
    const res = await fetch(
      `${API_BASE}/stripe-connect/approval/${encodeURIComponent(businessId)}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } }
    );
    const data = await parseJson(res);
    if (!res.ok) {
      return { success: false, error: data.error || `HTTP ${res.status}` };
    }
    return {
      success: true,
      approval: data.approval || null,
      canConnectStripe: !!data.canConnectStripe,
    };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Failed to load approval status' };
  }
}

export async function submitStripeConnectAccessRequest(
  businessId: string,
  merchantTerms: MerchantPaymentTermsAcceptance,
  requestNote?: string,
  snapshot?: StripeConnectRequestSnapshot
): Promise<{ success: boolean; error?: string; approval?: StripeConnectApprovalRecord }> {
  try {
    const res = await fetch(`${API_BASE}/stripe-connect/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        businessId,
        merchantTerms,
        requestNote: requestNote || '',
        requestSnapshot: snapshot || {},
      }),
    });
    const data = await parseJson(res);
    if (!res.ok) {
      return { success: false, error: data.error || 'Request failed' };
    }

    const owner = await AuthManager.getSavedBusinessOwner();
    if (owner?.id === businessId && data.approval) {
      await AuthManager.saveBusinessOwner({
        ...owner,
        stripeConnectApproval: data.approval,
      });
    }

    return { success: true, approval: data.approval };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Failed to submit request' };
  }
}

/** Load pending requests via admin API (preferred) or Firestore fallback. */
export async function fetchPendingStripeConnectRequests(): Promise<
  StripeConnectRequestListItem[]
> {
  const authHeader = await getAdminAuthHeader();
  if (authHeader.Authorization) {
    try {
      const res = await fetch(
        `${API_BASE}/admin/stripe-connect/requests?status=pending_review`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authHeader,
          },
        }
      );
      const data = await parseJson(res);
      if (res.ok && data.success && Array.isArray(data.requests)) {
        return data.requests.map((row: any) => ({
          businessId: row.businessId,
          businessName: row.businessName || 'Unnamed',
          approval: row.approval,
        }));
      }
      console.warn('fetchPendingStripeConnectRequests admin API:', data.error || res.status);
    } catch (e) {
      console.warn('fetchPendingStripeConnectRequests admin API failed:', e);
    }
  }

  return fetchPendingStripeConnectRequestsFromFirestore();
}

/** Load pending requests from Firestore (read-only; works when admin is signed in). */
export async function fetchPendingStripeConnectRequestsFromFirestore(): Promise<
  StripeConnectRequestListItem[]
> {
  const items: StripeConnectRequestListItem[] = [];

  try {
    if (Platform.OS === 'web') {
      const { initializeFirebase, getWebDb } = await import('./unifiedAuthService');
      if (!initializeFirebase()) return items;
      const db = getWebDb();
      if (!db) return items;
      const { collection, getDocs } = await import('firebase/firestore');
      const snap = await getDocs(collection(db, 'businesses'));
      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const approval = data.stripeConnectApproval as StripeConnectApprovalRecord | undefined;
        if (approval?.status === 'pending_review') {
          items.push({
            businessId: docSnap.id,
            businessName: data.name || data.businessName || 'Unnamed',
            approval,
          });
        }
      });
    } else {
      const { initializeFirebase, getMobileFirestore } = await import('./unifiedAuthService');
      if (!initializeFirebase()) return items;
      const mobileFirestore = getMobileFirestore?.();
      if (!mobileFirestore) return items;
      const db = mobileFirestore();
      const snap = await db.collection('businesses').get();
      snap.forEach((docSnap: any) => {
        const data = docSnap.data() || {};
        const approval = data.stripeConnectApproval as StripeConnectApprovalRecord | undefined;
        if (approval?.status === 'pending_review') {
          items.push({
            businessId: docSnap.id,
            businessName: data.name || data.businessName || 'Unnamed',
            approval,
          });
        }
      });
    }
  } catch (e) {
    console.warn('fetchPendingStripeConnectRequestsFromFirestore:', e);
  }

  return items.sort(
    (a, b) =>
      new Date(b.approval.requestedAt || 0).getTime() -
      new Date(a.approval.requestedAt || 0).getTime()
  );
}

export async function adminApproveStripeConnectRequest(
  businessId: string,
  checklist: Record<string, boolean>,
  adminNotes?: string,
  approvedBy?: string
): Promise<{ success: boolean; error?: string }> {
  const authHeader = await getAdminAuthHeader();
  if (!authHeader.Authorization) {
    return { success: false, error: 'Not signed in — sign in with an approved admin account.' };
  }

  try {
    const res = await fetch(
      `${API_BASE}/admin/stripe-connect/requests/${encodeURIComponent(businessId)}/approve`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
        body: JSON.stringify({ checklist, adminNotes, approvedBy }),
      }
    );
    const data = await parseJson(res);
    if (!res.ok) {
      return { success: false, error: data.error || 'Approve failed' };
    }
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Approve failed' };
  }
}

export async function adminRejectStripeConnectRequest(
  businessId: string,
  rejectionReason: string,
  rejectedBy?: string
): Promise<{ success: boolean; error?: string }> {
  const authHeader = await getAdminAuthHeader();
  if (!authHeader.Authorization) {
    return { success: false, error: 'Not signed in — sign in with an approved admin account.' };
  }

  try {
    const res = await fetch(
      `${API_BASE}/admin/stripe-connect/requests/${encodeURIComponent(businessId)}/reject`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
        body: JSON.stringify({ rejectionReason, rejectedBy }),
      }
    );
    const data = await parseJson(res);
    if (!res.ok) {
      return { success: false, error: data.error || 'Reject failed' };
    }
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Reject failed' };
  }
}

export async function adminSuspendStripeConnectAccess(
  businessId: string,
  reason?: string
): Promise<{ success: boolean; error?: string }> {
  const authHeader = await getAdminAuthHeader();
  if (!authHeader.Authorization) {
    return { success: false, error: 'Not signed in — sign in with an approved admin account.' };
  }

  try {
    const res = await fetch(
      `${API_BASE}/admin/stripe-connect/requests/${encodeURIComponent(businessId)}/suspend`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
        body: JSON.stringify({ reason }),
      }
    );
    const data = await parseJson(res);
    if (!res.ok) {
      return { success: false, error: data.error || 'Suspend failed' };
    }
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Suspend failed' };
  }
}

export function buildRequestSnapshotFromOwner(owner: BusinessOwner): StripeConnectRequestSnapshot {
  return {
    companyName: owner.companyName || owner.name,
    email: owner.email,
    phone: owner.phone,
    industry: owner.industry,
    website: owner.website,
  };
}

export function canConnectStripeFromApproval(
  approval?: StripeConnectApprovalRecord | null
): boolean {
  return approval?.status === 'approved';
}

export function approvalStatusLabel(status?: StripeConnectApprovalStatus): string {
  switch (status) {
    case 'pending_review':
      return 'Pending BookAPT review';
    case 'approved':
      return 'Approved — you may connect Stripe';
    case 'rejected':
      return 'Not approved';
    case 'suspended':
      return 'Payment access suspended';
    default:
      return 'Not submitted';
  }
}

export { MERCHANT_PAYMENT_TERMS_VERSION };
